// src/components/Scanner/ScannerModal.jsx
import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Paper,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Stack,
  Divider,
  Chip,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CardMedia,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DiamondIcon from "@mui/icons-material/Diamond";
import ScaleIcon from "@mui/icons-material/Scale";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SettingsIcon from "@mui/icons-material/Settings";
import ImageIcon from "@mui/icons-material/Image";
import { Scanner } from "@yudiel/react-qr-scanner";
import { useGoldRate } from "../context/GoldRateContext";

const ScannerModal = ({ open, onClose, onItemScanned, stock = [] }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { getRateByCarat, updateRates, rates } = useGoldRate();
  const [scanning, setScanning] = useState(true);
  const [scannedItem, setScannedItem] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [tempRates, setTempRates] = useState({
    "18carat": 4500,
    "22carat": 5500,
    "24carat": 6000,
  });

  // Load current rates when dialog opens
  useEffect(() => {
    if (rates) {
      setTempRates({
        "18carat": rates["18carat"] || 4500,
        "22carat": rates["22carat"] || 5500,
        "24carat": rates["24carat"] || 6000,
      });
    }
  }, [rates]);

  const parseAndFetchItem = async (qrCode) => {
    setLoading(true);
    try {
      const cleanQrCode = qrCode.trim();
      const foundItem = stock.find((item) => item.qr === cleanQrCode);

      if (!foundItem) {
        throw new Error(
          `Item with QR code "${cleanQrCode}" not found in inventory.`
        );
      }

      if (foundItem.sold === "Yes" || foundItem.sold === true) {
        throw new Error("This item has already been sold!");
      }

      await processFoundItem(foundItem);
    } catch (err) {
      setError(err.message);
      setScannedItem(null);
    } finally {
      setLoading(false);
    }
  };

  const processFoundItem = async (foundItem) => {
    let rate = 0;
    let caratValue = "";

    if (["Gold", "RoseGold", "WhiteGold"].includes(foundItem.metal)) {
      const melting = parseFloat(foundItem.melting);
      if (melting === 91.6 || melting === 92) {
        caratValue = "22carat";
      } else if (melting === 75) {
        caratValue = "18carat";
      } else if (melting === 99.9 || melting === 100) {
        caratValue = "24carat";
      } else {
        const carat = (melting / 100) * 24;
        caratValue = `${Math.round(carat)}carat`;
      }
      rate = getRateByCarat(caratValue);
    }

    // Calculate total price using the formula:
    // Price = (Weight * (100 + making charge) * Current Gold of the respective carat) * 1.03 / 100
    let currentPrice = 0;
    const makingChargesPercent = parseFloat(foundItem.makingCharges) || 0;

    if (rate > 0 && foundItem.weight) {
      const weight = parseFloat(foundItem.weight);
      const makingMultiplier = 100 + makingChargesPercent;
      const baseCalculation = weight * makingMultiplier * rate;
      currentPrice = (baseCalculation * 1.03) / 100;
    } else {
      currentPrice = foundItem.price || 0;
    }

    setScannedItem({
      id: foundItem.id || foundItem._id,
      qr: foundItem.qr,
      name: foundItem.name,
      metal: foundItem.metal,
      carat: caratValue,
      caratDisplay: caratValue.replace("carat", "K"),
      weight: foundItem.weight,
      grossWeight: foundItem.grossWeight,
      lessWeight: foundItem.lessWeight,
      huid: foundItem.huid,
      serialNo: foundItem.serialNo,
      currentRate: rate,
      currentPrice: currentPrice,
      makingCharges: makingChargesPercent,
      imageUrl: foundItem.imageUrl || null,
    });

    setError("");
  };

  const handleScan = (result) => {
    if (result && scanning && !loading) {
      setScanning(false);

      let scannedText = "";

      if (Array.isArray(result) && result.length > 0) {
        const firstResult = result[0];
        if (typeof firstResult === "object") {
          scannedText =
            firstResult.text ||
            firstResult.data ||
            firstResult.rawValue ||
            firstResult.toString();
        } else if (typeof firstResult === "string") {
          scannedText = firstResult;
        }
      } else if (typeof result === "object") {
        scannedText =
          result.text || result.data || result.rawValue || result.toString();
      } else if (typeof result === "string") {
        scannedText = result;
      }

      scannedText = scannedText?.toString().trim() || "";

      if (scannedText && scannedText.length > 0) {
        if (scannedText.startsWith("JWL")) {
          parseAndFetchItem(scannedText);
        } else {
          setError("Invalid QR code. Please scan a valid JewelStock QR code.");
          setTimeout(() => {
            setScanning(true);
            setError("");
          }, 3000);
        }
      } else {
        setError("No data detected. Please try scanning again.");
        setTimeout(() => {
          setScanning(true);
          setError("");
        }, 3000);
      }
    }
  };

  const handleError = (err) => {
    console.error("Scanner error:", err);
    setError("Camera access failed. Please check permissions.");
    setTimeout(() => {
      setScanning(true);
      setError("");
    }, 3000);
  };

  const handleClose = () => {
    setScanning(true);
    setScannedItem(null);
    setError("");
    setLoading(false);
    onClose();
  };

  const handleRetry = () => {
    setError("");
    setScanning(true);
    setScannedItem(null);
    setLoading(false);
  };

  const handleRateChange = (carat, value) => {
    setTempRates((prev) => ({
      ...prev,
      [carat]: parseFloat(value) || 0,
    }));
  };

  const handleSaveRates = () => {
    // Validate rates
    for (const [carat, rate] of Object.entries(tempRates)) {
      if (rate <= 0 || isNaN(rate)) {
        setError(`Please enter a valid rate for ${carat}`);
        return;
      }
    }

    updateRates(tempRates);
    setRateDialogOpen(false);

    // If an item is already scanned, recalculate its price
    if (scannedItem) {
      processFoundItem({
        ...scannedItem,
        weight: scannedItem.weight,
        makingCharges: scannedItem.makingCharges,
        metal: scannedItem.metal,
        melting: scannedItem.melting,
      });
    }
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Paper
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 550 },
            maxHeight: "90vh",
            p: 0,
            borderRadius: 2,
            overflow: "hidden",
            background: isDark
              ? alpha(theme.palette.background.paper, 0.95)
              : alpha("#ffffff", 0.98),
            backdropFilter: "blur(20px)",
            boxShadow: `0 25px 50px -12px ${alpha("#000000", 0.25)}`,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              background: `linear-gradient(135deg, ${alpha(
                isDark ? "#60A5FA" : "#2563EB",
                0.1
              )} 0%, ${alpha(isDark ? "#3B82F6" : "#1D4ED8", 0.05)} 100%)`,
              borderBottom: `1px solid ${alpha(
                isDark ? "#60A5FA" : "#2563EB",
                0.15
              )}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${
                    isDark ? "#60A5FA" : "#2563EB"
                  } 0%, ${isDark ? "#3B82F6" : "#1D4ED8"} 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 4px 14px 0 ${alpha(
                    isDark ? "#60A5FA" : "#2563EB",
                    0.4
                  )}`,
                }}
              >
                <QrCodeScannerIcon sx={{ fontSize: 24, color: "#FFFFFF" }} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    fontSize: "1.35rem",
                    background: `linear-gradient(135deg, ${
                      isDark ? "#60A5FA" : "#2563EB"
                    } 0%, ${isDark ? "#3B82F6" : "#1D4ED8"} 100%)`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  Scan Jewelry QR Code
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: isDark ? "#94A3B8" : "#64748B" }}
                >
                  Position QR code in the camera frame
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                onClick={() => setRateDialogOpen(true)}
                sx={{
                  borderRadius: 2,
                  backgroundColor: alpha(isDark ? "#1E293B" : "#F1F5F9", 0.6),
                  "&:hover": {
                    backgroundColor: alpha(isDark ? "#334155" : "#E2E8F0", 0.8),
                    transform: "scale(1.05)",
                  },
                }}
                title="Update Gold Rates"
              >
                <SettingsIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                onClick={handleClose}
                sx={{
                  borderRadius: 2,
                  backgroundColor: alpha(isDark ? "#1E293B" : "#F1F5F9", 0.6),
                  "&:hover": {
                    backgroundColor: alpha(isDark ? "#334155" : "#E2E8F0", 0.8),
                    transform: "scale(1.05)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3 }}>
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  backdropFilter: "blur(10px)",
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                }}
                action={
                  <Button color="inherit" size="small" onClick={handleRetry}>
                    Retry
                  </Button>
                }
              >
                {error}
              </Alert>
            )}

            {!scannedItem && scanning && !error && !loading && (
              <Box sx={{ position: "relative", minHeight: 450 }}>
                <Box
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    border: `2px solid ${alpha(
                      isDark ? "#60A5FA" : "#2563EB",
                      0.3
                    )}`,
                    boxShadow: `0 0 0 2px ${alpha(
                      isDark ? "#60A5FA" : "#2563EB",
                      0.1
                    )}`,
                  }}
                >
                  <Scanner
                    onScan={handleScan}
                    onError={handleError}
                    constraints={{ facingMode: "environment" }}
                    style={{ width: "100%" }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    mt: 2,
                    display: "block",
                    textAlign: "center",
                    color: isDark ? "#94A3B8" : "#64748B",
                  }}
                >
                  Position QR code in frame to scan
                </Typography>
              </Box>
            )}

            {loading && (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <CircularProgress
                  size={50}
                  sx={{
                    color: isDark ? "#60A5FA" : "#2563EB",
                    mb: 2,
                  }}
                />
                <Typography sx={{ color: isDark ? "#E2E8F0" : "#1E293B" }}>
                  Fetching item details...
                </Typography>
              </Box>
            )}

            {scannedItem && !loading && (
              <Box>
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(
                      isDark ? "#60A5FA" : "#2563EB",
                      0.05
                    )} 0%, ${alpha(
                      isDark ? "#3B82F6" : "#1D4ED8",
                      0.02
                    )} 100%)`,
                    border: `1px solid ${alpha(
                      isDark ? "#60A5FA" : "#2563EB",
                      0.2
                    )}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <CheckCircleIcon sx={{ color: "#10B981", fontSize: 20 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: "#10B981" }}
                    >
                      Item Scanned Successfully
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: isDark ? "#CBD5E1" : "#475569" }}
                  >
                    Please verify the details before adding to transaction
                  </Typography>
                </Box>

                {/* Item Header with Image */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 3,
                  }}
                >
                  {/* Product Image - 100px x 100px */}
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: 2,
                      overflow: "hidden",
                      border: `1px solid ${alpha(
                        isDark ? "#60A5FA" : "#2563EB",
                        0.2
                      )}`,
                      bgcolor: alpha(isDark ? "#1E293B" : "#F1F5F9", 0.5),
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {scannedItem.imageUrl ? (
                      <CardMedia
                        component="img"
                        image={scannedItem.imageUrl}
                        alt={scannedItem.name}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          p: 0.5,
                        }}
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/100x100?text=No+Image";
                        }}
                      />
                    ) : (
                      <ImageIcon
                        sx={{
                          fontSize: 40,
                          color: isDark ? "#475569" : "#94A3B8",
                        }}
                      />
                    )}
                  </Box>

                  {/* Item Title and Serial */}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        color: isDark ? "#F1F5F9" : "#0F172A",
                        mb: 0.5,
                      }}
                    >
                      {scannedItem.name} - {scannedItem.metal}
                    </Typography>
                    <Chip
                      label={`Serial: ${scannedItem.serialNo}`}
                      size="small"
                      sx={{
                        borderRadius: 2,
                        background: alpha(
                          isDark ? "#60A5FA" : "#2563EB",
                          0.15
                        ),
                        color: isDark ? "#60A5FA" : "#2563EB",
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Box>

                <Divider
                  sx={{
                    mb: 2,
                    borderColor: alpha(isDark ? "#60A5FA" : "#2563EB", 0.15),
                  }}
                />

                {/* Item Details Grid - Removed Price Sections */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <DiamondIcon
                      sx={{
                        fontSize: 20,
                        color: isDark ? "#60A5FA" : "#2563EB",
                      }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: isDark ? "#94A3B8" : "#64748B" }}
                      >
                        Carat
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: isDark ? "#F1F5F9" : "#0F172A",
                        }}
                      >
                        {scannedItem.caratDisplay || "N/A"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <ScaleIcon
                      sx={{
                        fontSize: 20,
                        color: isDark ? "#60A5FA" : "#2563EB",
                      }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: isDark ? "#94A3B8" : "#64748B" }}
                      >
                        Weight
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: isDark ? "#F1F5F9" : "#0F172A",
                        }}
                      >
                        {scannedItem.weight}g
                      </Typography>
                    </Box>
                  </Box>

                  {scannedItem.grossWeight && (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <ScaleIcon
                        sx={{
                          fontSize: 20,
                          color: isDark ? "#60A5FA" : "#2563EB",
                        }}
                      />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ color: isDark ? "#94A3B8" : "#64748B" }}
                        >
                          Gross Weight
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: isDark ? "#CBD5E1" : "#475569",
                          }}
                        >
                          {scannedItem.grossWeight}g
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {scannedItem.lessWeight && (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <ScaleIcon
                        sx={{
                          fontSize: 20,
                          color: isDark ? "#60A5FA" : "#2563EB",
                        }}
                      />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ color: isDark ? "#94A3B8" : "#64748B" }}
                        >
                          Less Weight
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: isDark ? "#CBD5E1" : "#475569",
                          }}
                        >
                          {scannedItem.lessWeight}g
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {scannedItem.makingCharges > 0 && (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <AttachMoneyIcon
                        sx={{
                          fontSize: 20,
                          color: isDark ? "#60A5FA" : "#2563EB",
                        }}
                      />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ color: isDark ? "#94A3B8" : "#64748B" }}
                        >
                          Making Charges
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: isDark ? "#F1F5F9" : "#0F172A",
                          }}
                        >
                          {scannedItem.makingCharges}%
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {scannedItem.huid && (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <DiamondIcon
                        sx={{
                          fontSize: 20,
                          color: isDark ? "#60A5FA" : "#2563EB",
                        }}
                      />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ color: isDark ? "#94A3B8" : "#64748B" }}
                        >
                          HUID
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: isDark ? "#CBD5E1" : "#475569",
                          }}
                        >
                          {scannedItem.huid}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleRetry}
                    fullWidth
                    startIcon={<QrCodeScannerIcon />}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      borderColor: alpha(isDark ? "#EF4444" : "#DC2626", 0.5),
                      color: isDark ? "#F87171" : "#DC2626",
                      "&:hover": {
                        borderColor: isDark ? "#EF4444" : "#DC2626",
                        backgroundColor: alpha(
                          isDark ? "#EF4444" : "#DC2626",
                          0.1
                        ),
                      },
                    }}
                  >
                    Scan Again
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleClose}
                    fullWidth
                    startIcon={<CloseIcon />}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      background: `linear-gradient(135deg, ${
                        isDark ? "#60A5FA" : "#2563EB"
                      } 0%, ${isDark ? "#3B82F6" : "#1D4ED8"} 100%)`,
                      boxShadow: `0 4px 14px 0 ${alpha(
                        isDark ? "#60A5FA" : "#2563EB",
                        0.4
                      )}`,
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: `0 6px 20px 0 ${alpha(
                          isDark ? "#60A5FA" : "#2563EB",
                          0.5
                        )}`,
                      },
                    }}
                  >
                    Close
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </Modal>

      {/* Rate Configuration Dialog */}
      <Dialog
        open={rateDialogOpen}
        onClose={() => setRateDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            background: isDark
              ? alpha(theme.palette.background.paper, 0.95)
              : alpha("#ffffff", 0.98),
            backdropFilter: "blur(20px)",
            border: `1px solid ${alpha(isDark ? "#60A5FA" : "#2563EB", 0.2)}`,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${alpha(
              isDark ? "#60A5FA" : "#2563EB",
              0.15
            )}`,
            pb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <SettingsIcon sx={{ color: isDark ? "#60A5FA" : "#2563EB" }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Update Gold Rates
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="18 Carat Gold Rate (per gram)"
              type="number"
              value={tempRates["18carat"]}
              onChange={(e) => handleRateChange("18carat", e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
                sx: {
                  borderRadius: 2,
                  backgroundColor: alpha(isDark ? "#1E293B" : "#F1F5F9", 0.5),
                },
              }}
            />
            <TextField
              label="22 Carat Gold Rate (per gram)"
              type="number"
              value={tempRates["22carat"]}
              onChange={(e) => handleRateChange("22carat", e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
                sx: {
                  borderRadius: 2,
                  backgroundColor: alpha(isDark ? "#1E293B" : "#F1F5F9", 0.5),
                },
              }}
            />
            <TextField
              label="24 Carat Gold Rate (per gram)"
              type="number"
              value={tempRates["24carat"]}
              onChange={(e) => handleRateChange("24carat", e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
                sx: {
                  borderRadius: 2,
                  backgroundColor: alpha(isDark ? "#1E293B" : "#F1F5F9", 0.5),
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setRateDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: alpha(isDark ? "#EF4444" : "#DC2626", 0.5),
              color: isDark ? "#F87171" : "#DC2626",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRates}
            variant="contained"
            sx={{
              borderRadius: 2,
              background: `linear-gradient(135deg, ${
                isDark ? "#60A5FA" : "#2563EB"
              } 0%, ${isDark ? "#3B82F6" : "#1D4ED8"} 100%)`,
            }}
          >
            Save Rates
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ScannerModal;